import {company_name, privacy_mail, location, contact_mail} from "./general";
import {primary} from "./colors";

export const privacy_sections = [
    {
        id: 1,
        title: "Introduction",
        content: (
            <p className="leading-8 text-gray-700 ">
                Welcome to {company_name}, a professional dental practice management platform
                developed and operated in the Kurdistan Region of Iraq. This Privacy Policy explains how we collect, use, store,
                share, and protect the information of users who access and use the LightDent dashboard and its associated services.
                By registering for or using LightDent, you agree to the terms of this Privacy Policy. If you do not agree, please
                discontinue use of the platform.
            </p>
        ),
    },

    {
        id: 2,
        title: "Who We Are",
        content: (
            <div>
                <p className="leading-8 text-gray-700 ">
                    LightDent is a dental practice management platform designed exclusively for licensed dental professionals, clinic
                    staff, dental laboratories, and dental product suppliers. We are based in the Kurdistan Region of Iraq and operate
                    in accordance with applicable local laws and regulations.
                </p>

                <div
                    className="mt-3 px-4 py-3 bg-gray-100 dark:bg-zinc-900 border-l-4"
                    style={{borderColor: primary}}
                >
                    <p className="text-md text-gray-700 ">
                        <strong>Email:</strong> {privacy_mail}
                    </p>

                    <p className="text-md text-gray-700  mt-1">
                        <strong>Location:</strong> {location}
                    </p>
                </div>
            </div>
        ),
    },

    {
        id: 3,
        title: "Who This Policy Applies To",
        content: (
            <div>
                <p className="text-gray-700 ">
                    This policy applies to all registered users of the {company_name} platform, including:
                </p>

                {[
                    ["Dentists", "Primary account holders managing their clinic"],
                    ["Dental Assistants & Clinic Staff", "Sub-users added by a dentist account"],
                    ["Dental Laboratories", "Lab partners connected through the platform"],
                    ["Companies & Suppliers", "Businesses listing or selling products on the LightDent marketplace"],
                ].map(([title, desc]) => (
                    <div
                        key={title}
                        className="flex gap-3 mt-3 items-start"
                    >
                        <div
                            className="min-w-[8px] h-2 rounded-full mt-2"
                            style={{background: primary}}
                        />

                        <p className="text-gray-700 ">
                            <strong>{title}</strong> — {desc}
                        </p>
                    </div>
                ))}
            </div>
        ),
    },

    {
        id: 4,
        title: "Information We Collect",
        content: (
            <div>
                {[
                    {
                        sub: "4.1 Account & Identity Information",
                        items: [
                            "Full name, professional title, and clinic name",
                            "Email address and phone number",
                            "Scanned ID or professional license documents (for identity verification)",
                            "Profile photo (optional)",
                        ],
                    },
                    {
                        sub: "4.2 Patient Data (Managed by Dentists)",
                        items: [
                            "Patient personal information (name, age, contact details)",
                            "Medical and dental history, anamnesis, diagnoses",
                            "Treatment plans, clinical notes, and prescriptions",
                            "Extraoral and intraoral clinical photographs",
                            "Dental X-rays and radiographic images",
                            "Consent forms and signed documents",
                        ],
                        note: "LightDent acts as a data processor for patient information. The dentist (clinic) is the data controller responsible for obtaining patient consent before entering their data into the platform.",
                    },
                    {
                        sub: "4.3 Professional & Clinic Activity Data",
                        items: [
                            "Appointment schedules and calendar events",
                            "Referral records (sent and received)",
                            "Laboratory work orders and communication",
                            "Stock and inventory entries",
                            "Course enrollments and educational activity",
                            "Cases, tips, and blog content published on the platform",
                        ],
                    },
                    {
                        sub: "4.4 Marketplace & Transaction Data",
                        items: [
                            "Products listed or purchased on the platform",
                            "Order history and delivery status",
                            "Offline payment records and invoice data",
                        ],
                    },
                    {
                        sub: "4.5 Technical & Usage Data",
                        items: [
                            "IP address and device type",
                            "Browser type and operating system",
                            "Pages visited and features used (via cookies and analytics)",
                            "Login timestamps and session activity",
                            "Error logs and performance data",
                        ],
                    },
                    {
                        sub: "4.6 Communication Data",
                        items: [
                            "Messages sent via the in-platform dentist chat",
                            "WhatsApp/SMS messages sent through integrated APIs",
                            "Support tickets and feedback submitted to our team",
                        ],
                    },
                ].map(({sub, items, note}) => (
                    <div key={sub} className="mb-8">
                        <p
                            className="font-semibold mb-3"
                            style={{color: primary}}
                        >
                            {sub}
                        </p>

                        {items.map(item => (
                            <div
                                key={item}
                                className="flex gap-3 mb-2 items-start"
                            >
                                <span
                                    className="mt-1"
                                    style={{color: primary}}
                                >
                                    •
                                </span>

                                <p className="text-sm text-gray-700 ">
                                    {item}
                                </p>
                            </div>
                        ))}

                        {note && (
                            <div className="mt-4 px-4 py-3 bg-yellow-500/10 border-l-4 border-yellow-500 text-sm text-gray-700 ">
                                <strong>Important:</strong> {note}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        ),
    },

    {
        id: 5,
        title: "How We Use Your Information",
        content: (
            <div>
                <p className="mb-4 text-gray-700 ">
                    We use collected data for the following purposes:
                </p>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr
                                className="border-b"
                                style={{
                                    background: `${primary}15`,
                                    borderColor: `${primary}30`,
                                }}
                            >
                                <th className="px-4 py-3 text-left">
                                    Purpose
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Data Used
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {[
                                ["Provide and operate the platform", "Account, activity, and patient data"],
                                ["Verify professional identity", "ID documents, license info"],
                                ["Enable appointment and patient management", "Schedules, patient records"],
                                ["Send notifications and reminders", "Phone number, email, WhatsApp"],
                                ["Deliver AI-powered clinical assistance", "Anonymized query data"],
                                ["Improve platform performance", "Usage analytics, error logs"],
                                ["Process orders and manage marketplace", "Order and product data"],
                                ["Ensure security and prevent fraud", "IP address, login activity"],
                                ["Respond to support requests", "Contact and communication data"],
                                ["Comply with legal obligations", "All relevant data as required"],
                            ].map(([purpose, data], i) => (
                                <tr
                                    key={i}
                                    className="border-b border-gray-200 dark:border-gray-800"
                                >
                                    <td className="px-4 py-3 text-gray-700 ">
                                        {purpose}
                                    </td>

                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                                        {data}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p
                    className="mt-4 text-sm"
                >
                    We do <strong>not</strong> use your data for advertising, and we do <strong>not</strong> sell your data to third parties.
                </p>
            </div>
        ),
    },

    {
        id: 6,
        title: "Third-Party Services",
        content: (
            <div>
                <p className="mb-5 text-gray-700 ">
                    To deliver certain features, LightDent integrates with the following categories of third-party services. Each operates under its own privacy policy:
                </p>

                {[
                    {
                        title: "6.1 Analytics",
                        text: "We use web analytics tools (such as Google Analytics) to understand how the platform is used. This data is aggregated and anonymized. You can opt out via your browser's cookie settings.",
                    },
                    {
                        title: "6.2 AI Services",
                        text: "The AI-LightDent Assistant is powered by third-party AI providers. Queries submitted to the assistant may be processed by these providers. We do not send identifiable patient data to AI services — queries are anonymized before processing.",
                    },
                    {
                        title: "6.3 WhatsApp / SMS APIs",
                        text: "We use third-party messaging APIs to send appointment reminders, confirmations, and notifications to patients on behalf of dentists. Only the phone number and message content necessary for delivery are shared with these providers.",
                    },
                ].map(({title, text}) => (
                    <div
                        key={title}
                        className="mb-4 p-4 border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-zinc-900"
                    >
                        <p
                            className="font-semibold mb-2"
                            style={{color: primary}}
                        >
                            {title}
                        </p>

                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {text}
                        </p>
                    </div>
                ))}
            </div>
        ),
    },

    {
        id: 7,
        title: "Cookies",
        content: (
            <div>
                <p className="mb-4 text-gray-700 ">
                    LightDent uses cookies and similar tracking technologies to:
                </p>

                {[
                    "Keep you logged in across sessions",
                    "Remember your preferences (language, theme)",
                    "Collect anonymous usage analytics",
                    "Improve platform performance",
                ].map(item => (
                    <div
                        key={item}
                        className="flex gap-3 mb-2 items-start"
                    >
                        <span
                            className="mt-1"
                            style={{color: primary}}
                        >
                            •
                        </span>

                        <p className="text-sm text-gray-700 ">
                            {item}
                        </p>
                    </div>
                ))}

                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    You can manage or disable cookies through your browser settings. Note that disabling certain cookies may affect platform functionality.
                </p>
            </div>
        ),
    },

    {
        id: 8,
        title: "Data Storage & Security",
        content: (
            <div>
                {[
                    "All data is stored on secured servers with encryption at rest and in transit (SSL/TLS).",
                    "Access to sensitive data (patient records, X-rays, ID documents) is restricted to authorized users only.",
                    "Staff accounts have role-based permissions controlled by the primary dentist account.",
                    "We conduct regular security reviews and apply industry-standard protections.",
                    "In the event of a data breach that affects your information, we will notify affected users as soon as reasonably possible.",
                ].map((item, i) => (
                    <div
                        key={i}
                        className="flex gap-4 mb-4 items-start"
                    >
                        <div
                            className="min-w-[24px] h-6 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{
                                background: `${primary}20`,
                                color: primary,
                            }}
                        >
                            {i + 1}
                        </div>

                        <p className="text-sm text-gray-700 ">
                            {item}
                        </p>
                    </div>
                ))}
            </div>
        ),
    },

    {
        id: 9,
        title: "Data Retention",
        content: (
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr
                            className="border-b"
                            style={{
                                background: `${primary}15`,
                                borderColor: `${primary}30`,
                            }}
                        >
                            <th className="px-4 py-3 text-left">
                                Data Type
                            </th>

                            <th className="px-4 py-3 text-left">
                                Retention Period
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {[
                            ["Account information", "Until account deletion + 1 year"],
                            ["Patient records", "Until dentist deletes them or account is closed"],
                            ["X-rays & clinical images", "Until manually deleted by dentist"],
                            ["ID verification documents", "1 year after verification"],
                            ["Usage & analytics data", "12 months rolling"],
                            ["Support communications", "2 years"],
                        ].map(([type, period], i) => (
                            <tr
                                key={i}
                                className="border-b border-gray-200 dark:border-gray-800"
                            >
                                <td className="px-4 py-3 text-gray-700 ">
                                    {type}
                                </td>

                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                                    {period}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ),
    },

    {
        id: 10,
        title: "Sharing of Information",
        content: (
            <div>
                <p className="mb-4 text-gray-700 ">
                    We do not sell, rent, or trade your personal information. We may share data only in the following limited circumstances:
                </p>

                {[
                    ["With your explicit consent", "e.g., sharing referral data with another dentist on the platform"],
                    ["With third-party service providers", "Only as described in Section 6, and only to the extent necessary"],
                    ["With dental laboratories", "Case and work order data shared as directed by the dentist"],
                    ["For legal compliance", "If required by Iraqi law or a valid legal order"],
                    ["To protect rights and safety", "To prevent fraud, abuse, or harm to users or the platform"],
                ].map(([title, desc]) => (
                    <div
                        key={title}
                        className="flex gap-3 mb-3 items-start"
                    >
                        <span
                            className="mt-1"
                            style={{color: primary}}
                        >
                            •
                        </span>

                        <p className="text-sm text-gray-700 ">
                            <strong>{title}</strong> — {desc}
                        </p>
                    </div>
                ))}
            </div>
        ),
    },

    {
        id: 11,
        title: "Your Rights",
        content: (
            <div>
                <p className="mb-4 text-gray-700 ">
                    As a LightDent user, you have the right to:
                </p>

                {[
                    ["Access", "Your personal data stored on the platform"],
                    ["Correct", "Inaccurate or outdated information"],
                    ["Delete", "Your account and associated data"],
                    ["Export", "Your data in a portable format"],
                    ["Withdraw consent", "For optional data processing at any time"],
                    ["Opt out", "Of analytics tracking via cookie settings"],
                ].map(([right, desc]) => (
                    <div
                        key={right}
                        className="flex gap-4 mb-3 p-3 rounded-lg bg-gray-100 dark:bg-zinc-900"
                    >
                        <span
                            className="font-semibold min-w-[110px]"
                            style={{color: primary}}
                        >
                            {right}
                        </span>

                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {desc}
                        </p>
                    </div>
                ))}

                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    To exercise any of these rights, contact us at{" "}
                    <span style={{color: primary}}>
                        {privacy_mail}
                    </span>.
                </p>
            </div>
        ),
    },

    {
        id: 12,
        title: "Children & Minors",
        content: (
            <p className="leading-8 text-gray-700 ">
                LightDent is a professional platform intended solely for use by licensed dental professionals and verified business
                entities. It is not directed at individuals under the age of 18. We do not knowingly collect personal data from minors.
            </p>
        ),
    },

    {
        id: 13,
        title: "Contact Us",
        content: (
            <div>
                <p className="mb-4 text-gray-700 ">
                    If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your data, please contact us:
                </p>

                <div className="p-4 border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-zinc-900">
                    <p className="text-md text-gray-700 ">
                        {contact_mail}
                    </p>

                    <p className="text-md text-gray-700  mt-1">
                        {location}
                    </p>
                </div>
            </div>
        ),
    },

    {
        id: 14,
        title: "Changes to This Policy",
        content: (
            <div>
                <p className="leading-8 text-gray-700 ">
                    We may update this Privacy Policy from time to time to reflect changes in our platform or legal requirements.
                    When we do, we will update the "Last Updated" date at the top of this page and notify registered users via
                    email or an in-platform notification. Continued use of LightDent after changes are posted constitutes
                    acceptance of the updated policy.
                </p>

                <div className="mt-4 px-4 py-3 bg-yellow-500/10 border-l-4 border-yellow-500 text-sm text-gray-700 ">
                    This Privacy Policy was prepared specifically for LightDent. It is recommended that you have this document reviewed by a qualified legal professional before publishing.
                </div>
            </div>
        ),
    },
];